import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { PreordersTableTab } from '@ekhodealer/ekho-common';
import type { PreorderDetailsTab } from '../atoms/preorderDetailsDrawerAtoms';

import { paths } from '@config/paths';

import { PREORDER_DETAILS_TAB } from '../atoms/preorderDetailsDrawerAtoms';
import { currentPageAtom, currentPreordersListAtom, itemsPerPageAtom } from '../atoms/preordersAtoms';

interface PreorderNavigationState {
	canGoPrevious: boolean;
	canGoNext: boolean;
	currentIndex: number;
	totalInCurrentPage: number;
}

interface PreorderNavigationReturn {
	openPreorderDetails: (preorderId: string, detailsTab?: PreorderDetailsTab) => void;
	closePreorderDetails: () => void;
	navigateToPreviousPreorder: (currentPreorderId: string, detailsTab?: PreorderDetailsTab) => void;
	navigateToNextPreorder: (currentPreorderId: string, detailsTab?: PreorderDetailsTab) => void;
	getNavigationState: (currentPreorderId: string, totalCount: number) => PreorderNavigationState;
	servicesProviderId: string | undefined;
	tabId: string | undefined;
	preorderId: string | undefined;
}

/**
 * Custom hook for preorder-related navigation
 * Encapsulates the logic for navigating to preorder details and closing the drawer
 *
 * @example
 * ```tsx
 * const { openPreorderDetails, closePreorderDetails, navigateToPreviousPreorder, navigateToNextPreorder } = usePreorderNavigation();
 *
 * // Open preorder details to a specific tab
 * openPreorderDetails('preorder-123', PREORDER_DETAILS_TAB.DETAILS);
 *
 * // Navigate to adjacent preorders
 * navigateToPreviousPreorder('preorder-123');
 * navigateToNextPreorder('preorder-123');
 *
 * // Close the drawer
 * closePreorderDetails();
 * ```
 */
export function usePreorderNavigation(): PreorderNavigationReturn {
	const navigate = useNavigate();
	const location = useLocation();
	const { servicesProviderId, tabId, preorderId } = useParams<{
		servicesProviderId: string;
		tabId: string;
		preorderId?: string;
	}>();

	// Get current preorders list and pagination state
	const currentPreordersList = useAtomValue(currentPreordersListAtom);
	const currentPage = useAtomValue(currentPageAtom);
	const itemsPerPage = useAtomValue(itemsPerPageAtom);
	const setCurrentPage = useSetAtom(currentPageAtom);

	// Track pending navigation after page change
	const pendingNavigationRef = useRef<{
		action: 'next' | 'prev';
		targetTab: PreorderDetailsTab;
	} | null>(null);

	/**
	 * Navigate to preorder details with a specific tab
	 * @param preorderId - The ID of the preorder to open
	 * @param detailsTab - The tab to open (defaults to DETAILS)
	 */
	const openPreorderDetails = useCallback(
		(preorderId: string, detailsTab: PreorderDetailsTab = PREORDER_DETAILS_TAB.DETAILS) => {
			if (!servicesProviderId || !tabId) {
				console.warn(
					'Cannot navigate to preorder details: missing servicesProviderId or tabId',
				);
				return;
			}

			// Preserve query parameters (filters, pagination, etc.) when opening details
			const path = paths.preorderDetails.getHref(
				servicesProviderId,
				tabId as PreordersTableTab,
				preorderId,
				detailsTab,
			);
			navigate(path + location.search);
		},
		[navigate, servicesProviderId, tabId, location.search],
	);

	/**
	 * Close the preorder details drawer and return to the preorders list
	 */
	const closePreorderDetails = (): void => {
		if (!servicesProviderId || !tabId) {
			console.warn('Cannot close preorder details: missing servicesProviderId or tabId');
			return;
		}

		// Preserve query parameters (filters, pagination, etc.) when closing details
		const path = paths.preordersTab.getHref(servicesProviderId, tabId as PreordersTableTab);
		navigate(path + location.search);
	};

	/**
	 * Navigate to the previous preorder in the list
	 * Handles pagination automatically if needed
	 * @param currentPreorderId - The ID of the currently displayed preorder
	 * @param detailsTab - The tab to maintain when navigating (defaults to DETAILS)
	 */
	const navigateToPreviousPreorder = (
		currentPreorderId: string,
		detailsTab: PreorderDetailsTab = PREORDER_DETAILS_TAB.DETAILS,
	): void => {
		const currentIndex = currentPreordersList.indexOf(currentPreorderId);

		if (currentIndex > 0) {
			// Previous preorder is on current page
			const prevPreorderId = currentPreordersList[currentIndex - 1];
			openPreorderDetails(prevPreorderId, detailsTab);
		} else if (currentIndex === 0 && currentPage > 1) {
			// Need to go to previous page - go to last item of previous page
			pendingNavigationRef.current = { action: 'prev', targetTab: detailsTab };
			setCurrentPage(currentPage - 1);
		}
		// If at the beginning of first page, do nothing
	};

	/**
	 * Navigate to the next preorder in the list
	 * Handles pagination automatically if needed
	 * @param currentPreorderId - The ID of the currently displayed preorder
	 * @param detailsTab - The tab to maintain when navigating (defaults to DETAILS)
	 */
	const navigateToNextPreorder = (
		currentPreorderId: string,
		detailsTab: PreorderDetailsTab = PREORDER_DETAILS_TAB.DETAILS,
	): void => {
		const currentIndex = currentPreordersList.indexOf(currentPreorderId);

		if (currentIndex >= 0 && currentIndex < currentPreordersList.length - 1) {
			// Next preorder is on current page
			const nextPreorderId = currentPreordersList[currentIndex + 1];
			openPreorderDetails(nextPreorderId, detailsTab);
		} else if (currentIndex === currentPreordersList.length - 1) {
			// Need to go to next page - go to first item of next page
			pendingNavigationRef.current = { action: 'next', targetTab: detailsTab };
			setCurrentPage(currentPage + 1);
		}
		// If at the end of results, do nothing (boundary check should disable button)
	};

	/**
	 * Get navigation state for the current preorder
	 * Returns information about whether prev/next navigation is possible
	 */
	const getNavigationState = (currentPreorderId: string, totalCount: number): {
		canGoPrevious: boolean;
		canGoNext: boolean;
		currentIndex: number;
		totalInCurrentPage: number;
	} => {
		const currentIndex = currentPreordersList.indexOf(currentPreorderId);
		const isFirstOnPage = currentIndex === 0;
		const isLastOnPage = currentIndex === currentPreordersList.length - 1;
		const isFirstPage = currentPage === 1;
		const totalPages = Math.ceil(totalCount / itemsPerPage);
		const isLastPage = currentPage >= totalPages;

		return {
			canGoPrevious: !(isFirstOnPage && isFirstPage),
			canGoNext: !(isLastOnPage && isLastPage),
			currentIndex,
			totalInCurrentPage: currentPreordersList.length,
		};
	};

	// Effect to handle navigation after page change
	useEffect(() => {
		if (pendingNavigationRef.current && currentPreordersList.length > 0) {
			const { action, targetTab } = pendingNavigationRef.current;

			if (action === 'next') {
				// Navigate to first preorder of new page
				openPreorderDetails(currentPreordersList[0], targetTab);
			} else if (action === 'prev') {
				// Navigate to last preorder of new page
				openPreorderDetails(currentPreordersList[currentPreordersList.length - 1], targetTab);
			}

			// Clear pending navigation
			pendingNavigationRef.current = null;
		}
	}, [currentPreordersList, openPreorderDetails]);

	return {
		openPreorderDetails,
		closePreorderDetails,
		navigateToPreviousPreorder,
		navigateToNextPreorder,
		getNavigationState,
		// Expose params in case components need them
		servicesProviderId,
		tabId,
		preorderId,
	};
}
